//@flow
import React, {useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {makeStyles, MenuItem, Typography, Chip} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import CustomSelect from '../../../../../../../elements/CustomSelect/CustomSelect';
import {TRIGGER_FIELDS, TRIGGER_TYPE_OPTIONS} from '../../const';
import FilterCard from './FilterCard';
import {getFilterComponent} from './registry';
import {
  TRIGGER_FILTER_LABELS,
  applyTriggerTypeChange,
  getDefaultExecutionTimeSlot,
  getDefaultFilterValues,
  isCustomerInactivityTrigger,
  isDefaultFilter,
  orderActiveFilterTypes,
  supportsMultipleFilters,
  DEFAULT_TRIGGER_TYPE,
} from '../../../../utils/triggerConfig';
import {
  getActiveFilterTypes,
  getOptionalFiltersToAdd,
} from '../../../../utils/validation';
import {unregisterFilterFields} from '../../../../utils/filterFields';
import {getFormError} from '../../../../utils/formErrors';
import type {
  TriggerFilterType,
  TriggerType,
} from '../../../../../../../entities';
import {colors} from '../../../../../../../assets/styles/colors';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  filtersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  addFilterRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
  },
  chip: {
    fontWeight: 600,
    cursor: 'pointer',
  },
  chipSelected: {
    backgroundColor: colors.purple3,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.purple3,
    },
  },
  optionalChip: {
    borderStyle: 'dashed',
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
  },
  emptyHint: {
    fontSize: '.8125rem',
    color: colors.grey700,
  },
}));

const setValueOptions = {shouldDirty: true, shouldValidate: true};

type Props = {
  /** When false (edit mode), the trigger type selector is disabled. */
  canChangeTriggerType?: boolean,
};

export default function FiltersSection({canChangeTriggerType = true}: Props) {
  const classes = useStyles();
  const {
    register,
    watch,
    setValue,
    getValues,
    unregister,
    errors,
  } = useFormContext();
  const [activeFilterTypes, setActiveFilterTypes] = useState<
    TriggerFilterType[]
  >([]);
  const filtersRevisionRef = useRef(0);

  const triggerType: TriggerType = watch(
    TRIGGER_FIELDS.triggerType,
    DEFAULT_TRIGGER_TYPE
  );
  const filtersFromForm = watch(TRIGGER_FIELDS.filters);

  useEffect(() => {
    register(TRIGGER_FIELDS.triggerType);
    register(TRIGGER_FIELDS.filters);
  }, [register]);

  useEffect(() => {
    const filters = filtersFromForm || getValues(TRIGGER_FIELDS.filters) || {};
    const types = orderActiveFilterTypes(
      triggerType,
      getActiveFilterTypes(filters)
    );

    if (types.length === 0) return;

    setActiveFilterTypes(prev => {
      const isSame =
        prev.length === types.length &&
        prev.every((type, index) => type === types[index]);

      return isSame ? prev : types;
    });
  }, [filtersFromForm, getValues, triggerType]);

  const readCurrentFilters = () => {
    // After setFilterFieldUpdates keeps parent `filters` current, full
    // getValues().filters matches nested edits (RHF v6 parent/nested quirk).
    const allValues = getValues() || {};
    return allValues[TRIGGER_FIELDS.filters] || {};
  };

  const syncFiltersToForm = (
    nextFilterTypes: TriggerFilterType[],
    nextFilters: {[key: TriggerFilterType]: any}
  ) => {
    filtersRevisionRef.current += 1;
    setActiveFilterTypes(orderActiveFilterTypes(triggerType, nextFilterTypes));
    setValue(TRIGGER_FIELDS.filters, nextFilters, setValueOptions);
  };

  const handleTriggerTypeChange = (nextTriggerType: TriggerType) => {
    if (!canChangeTriggerType) return;

    const currentFilters = readCurrentFilters();

    Object.keys(currentFilters).forEach(key => {
      unregisterFilterFields((key: any), unregister);
    });

    const nextFilters = applyTriggerTypeChange(nextTriggerType);

    setValue(TRIGGER_FIELDS.triggerType, nextTriggerType, setValueOptions);

    if (isCustomerInactivityTrigger(nextTriggerType)) {
      setValue(
        TRIGGER_FIELDS.executionTimeSlot,
        getDefaultExecutionTimeSlot(),
        setValueOptions
      );
    }

    filtersRevisionRef.current += 1;
    setActiveFilterTypes(
      orderActiveFilterTypes(nextTriggerType, getActiveFilterTypes(nextFilters))
    );
    setValue(TRIGGER_FIELDS.filters, nextFilters, setValueOptions);
  };

  const handleAddFilter = (filterType: TriggerFilterType) => {
    if (!supportsMultipleFilters(triggerType)) return;
    if (activeFilterTypes.includes(filterType)) return;

    const currentFilters = readCurrentFilters();
    const nextFilters: any = {...currentFilters};
    nextFilters[filterType] = getDefaultFilterValues(filterType);

    syncFiltersToForm([...activeFilterTypes, filterType], nextFilters);
  };

  const handleRemoveFilter = (filterType: TriggerFilterType) => {
    unregisterFilterFields(filterType, unregister);

    const currentFilters = readCurrentFilters();
    const nextFilters: any = {};

    Object.keys(currentFilters).forEach(key => {
      if (key !== filterType) {
        nextFilters[key] = currentFilters[key];
      }
    });

    syncFiltersToForm(
      activeFilterTypes.filter(type => type !== filterType),
      nextFilters
    );
  };

  const optionalFiltersToAdd = getOptionalFiltersToAdd(
    triggerType,
    activeFilterTypes
  );
  const filtersError = getFormError(errors, TRIGGER_FIELDS.filters);

  return (
    <div className={classes.root}>
      <CustomSelect
        placeholder="Seleccionar tipo de disparador"
        value={triggerType}
        disabled={!canChangeTriggerType}
        onChange={event => {
          handleTriggerTypeChange(event.target.value);
        }}
      >
        {TRIGGER_TYPE_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      {!canChangeTriggerType && (
        <Typography variant="body2" className={classes.emptyHint}>
          El tipo de disparador no se puede cambiar en un trigger existente.
        </Typography>
      )}

      {filtersError && (
        <Typography className={classes.fieldError}>{filtersError}</Typography>
      )}

      <div className={classes.filtersList}>
        {activeFilterTypes.map(filterType => {
          const FilterComponent = getFilterComponent(filterType);
          const canRemove = !isDefaultFilter(triggerType, filterType);

          return (
            <FilterCard
              key={`${filterType}-${filtersRevisionRef.current}`}
              title={TRIGGER_FILTER_LABELS[filterType]}
              canRemove={canRemove}
              onRemove={() => handleRemoveFilter(filterType)}
            >
              <FilterComponent filterType={(filterType: any)} />
            </FilterCard>
          );
        })}
      </div>

      {optionalFiltersToAdd.length > 0 && (
        <div className={classes.addFilterRow}>
          <Typography variant="body2">Agregar filtro opcional</Typography>
          <div className={classes.chipsRow}>
            {optionalFiltersToAdd.map(filterType => (
              <Chip
                key={filterType}
                icon={<Add fontSize="small" />}
                label={TRIGGER_FILTER_LABELS[filterType]}
                clickable
                variant="outlined"
                className={`${classes.chip} ${classes.optionalChip}`}
                onClick={() => handleAddFilter(filterType)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
